import React, { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Collapse,
  Button,
  Grid,
  Paper,
  Fade,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setSelectedCategory } from '@e-learning/shared/src/store/slices/coursesSlice';

const CategoryFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const { courses, selectedCategory } = useAppSelector((state) => state.courses);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Get unique categories from courses
  const categories = useMemo(
    () => Array.from(new Set(courses.map((c) => c.category))).sort(),
    [courses]
  );

  // Filter categories based on search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase();
    return categories.filter((cat) => cat.toLowerCase().includes(query));
  }, [categories, searchQuery]);

  // Show first 12 categories by default, rest when expanded
  const visibleCategories = showAll ? filteredCategories : filteredCategories.slice(0, 12);
  const hasMoreCategories = filteredCategories.length > 12;

  const handleCategoryClick = (category: string | null) => {
    dispatch(setSelectedCategory(category));
  };

  const getFilteredCount = (category: string | null) => {
    if (category === null) return courses.length;
    return courses.filter((c) => c.category === category).length;
  };

  const isSelected = (category: string | null) => selectedCategory === category;

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearFilter = () => {
    dispatch(setSelectedCategory(null));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.1)',
        mb: 4,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FilterListIcon sx={{ color: '#ffffff', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Filter by Category
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {selectedCategory
                ? `${getFilteredCount(selectedCategory)} course${getFilteredCount(selectedCategory) !== 1 ? 's' : ''} found`
                : `Browse ${courses.length} courses across ${categories.length} categories`}
            </Typography>
          </Box>
        </Box>
        {selectedCategory && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilter}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Clear Filter
          </Button>
        )}
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search categories..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={handleClearSearch}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                <ClearIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </Button>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: 'background.paper',
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
              borderWidth: 2,
            },
          },
        }}
      />

      {/* Filter Chips */}
      <Box>
        <Grid container spacing={1.5}>
          {/* All Button */}
          <Grid item>
            <Fade in={true}>
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      All
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        backgroundColor: isSelected(null)
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'rgba(99, 102, 241, 0.1)',
                        color: isSelected(null) ? '#ffffff' : 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    >
                      {getFilteredCount(null)}
                    </Typography>
                  </Box>
                }
                onClick={() => handleCategoryClick(null)}
                sx={{
                  px: 2,
                  py: 1.5,
                  height: 'auto',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  ...(isSelected(null)
                    ? {
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        color: '#ffffff',
                        boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
                        transform: 'scale(1.02)',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(99, 102, 241, 0.5)',
                          transform: 'scale(1.05)',
                        },
                      }
                    : {
                        background: 'background.paper',
                        border: '2px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        '&:hover': {
                          background: 'rgba(99, 102, 241, 0.08)',
                          borderColor: 'primary.main',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                        },
                      }),
                }}
              />
            </Fade>
          </Grid>

          {/* Category Chips */}
          {visibleCategories.map((category, index) => (
            <Grid item key={category}>
              <Fade in={true} timeout={300} style={{ transitionDelay: `${index * 20}ms` }}>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {category}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          backgroundColor: isSelected(category)
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(99, 102, 241, 0.1)',
                          color: isSelected(category) ? '#ffffff' : 'primary.main',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      >
                        {getFilteredCount(category)}
                      </Typography>
                    </Box>
                  }
                  onClick={() => handleCategoryClick(category)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    height: 'auto',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    ...(isSelected(category)
                      ? {
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          color: '#ffffff',
                          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
                          transform: 'scale(1.02)',
                          '&:hover': {
                            boxShadow: '0 6px 20px rgba(99, 102, 241, 0.5)',
                            transform: 'scale(1.05)',
                          },
                        }
                      : {
                          background: 'background.paper',
                          border: '2px solid',
                          borderColor: 'divider',
                          color: 'text.primary',
                          '&:hover': {
                            background: 'rgba(99, 102, 241, 0.08)',
                            borderColor: 'primary.main',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                          },
                        }),
                  }}
                />
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Show More/Less Button */}
        {hasMoreCategories && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="text"
              onClick={() => setShowAll(!showAll)}
              endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'primary.main',
                '&:hover': {
                  background: 'rgba(99, 102, 241, 0.08)',
                },
              }}
            >
              {showAll
                ? `Show Less (${filteredCategories.length - 12} hidden)`
                : `Show All (${filteredCategories.length - 12} more)`}
            </Button>
          </Box>
        )}

        {/* No Results Message */}
        {searchQuery && filteredCategories.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              No categories found matching "{searchQuery}"
            </Typography>
            <Button
              variant="text"
              onClick={handleClearSearch}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Clear search
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default CategoryFilter;

